import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Leaf, Globe, Recycle, Package, ChevronRight, Download, Truck, Users, Mail, Phone } from "lucide-react";
import { trackCTAClick } from "@/lib/analytics";
import { Helmet } from "react-helmet-async";

const CATALOGUE_URL = "/Prithira-Global-Catalogue-2026.pdf";

const IMAGES = {
  hero: "/images/home-hero.jpg",
  bagasse: "/images/home-bagasse.jpg",
  areca: "/images/home-areca.jpg",
  bioBags: "/bio-carry-bag.jpg",
  bioCutlery: "/products/bio-cutlery.jpg",
  coconutScrubber: "/images/home-scrubber.jpg",
  sustainability: "/images/home-sustainability.jpg",
};

const HomePage = () => {
  const handleCTAClick = (ctaName) => {
    trackCTAClick(ctaName, "/");
  };

  return (
    <div data-testid="home-page" className="noise-overlay">
      <Helmet>
        <title>Eco-Friendly Packaging Supplier | Prithira Global India</title>
        <meta name="description" content="Prithira Global - Leading supplier and exporter of eco-friendly biodegradable packaging solutions including bagasse tableware, areca leaf plates, biodegradable bags, and coconut scrubbers from India." />
        <meta name="keywords" content="eco friendly packaging supplier India, biodegradable tableware exporter, bagasse plates supplier, areca leaf products exporter, sustainable packaging India" />
        <link rel="canonical" href="https://www.prithiraglobal.com/" />
        <meta property="og:title" content="Eco-Friendly Packaging Supplier | Prithira Global India" />
        <meta property="og:description" content="Leading supplier and exporter of biodegradable tableware, areca leaf plates, and sustainable packaging solutions from India." />
        <meta property="og:url" content="https://www.prithiraglobal.com/" />
      </Helmet>

      {/* Hero Section */}
      <section
        data-testid="hero-section"
        className="relative min-h-screen flex items-center overflow-hidden"
      >
        {/* Background Image */}
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(${IMAGES.hero})` }}
        >
          {/* Mobile overlay: stronger, full-width wash for legibility on small screens */}
          <div className="absolute inset-0 bg-gradient-to-b from-stone-50/95 via-stone-50/85 to-stone-50/70 sm:hidden" />
          {/* Tablet/Desktop overlay: left-to-right fade so product imagery shows on the right */}
          <div className="hidden sm:block absolute inset-0 bg-gradient-to-r from-stone-50/90 via-stone-50/70 to-transparent" />
        </div>

        {/* Hero Content */}
        <div className="container-custom relative z-10 pt-32 pb-20">
          <div className="max-w-3xl">
            <span className="caption text-primary mb-6 block opacity-0 animate-fade-up stagger-1">
              Eco-Friendly Packaging Supplier India
            </span>
            <h1 className="heading-xl text-stone-900 mb-8 opacity-0 animate-fade-up stagger-2">
              Sustainable Packaging Solutions for a{" "}
              <span className="font-heading italic text-primary">Greener</span> Future
            </h1>
            <p className="body-lg text-stone-600 mb-10 max-w-2xl opacity-0 animate-fade-up stagger-3">
              Eco-friendly bagasse tableware, areca leaf products, biodegradable bags, 
              and coconut scrubbers—designed for businesses transitioning away from plastic.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 opacity-0 animate-fade-up stagger-4">
              <Link to="/partner" onClick={() => handleCTAClick("hero_partner")}>
                <Button
                  data-testid="hero-partner-btn"
                  className="rounded-full px-8 py-6 text-base font-medium tracking-wide bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg hover:shadow-xl btn-lift"
                >
                  Partner With Us
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <a
                href={CATALOGUE_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => handleCTAClick("hero_catalogue")}
              >
                <Button
                  data-testid="hero-catalogue-btn"
                  variant="outline"
                  className="rounded-full px-8 py-6 text-base font-medium tracking-wide border-2 border-primary text-primary hover:bg-primary/5"
                >
                  <Download className="mr-2 h-5 w-5" />
                  Get Catalogue
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* About Intro Section */}
      <section className="section-spacing bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center mb-16">
            <h2 className="heading-lg text-stone-900 mb-6">
              Your Trusted Partner for <span className="italic">Sustainable</span> Packaging
            </h2>
            <p className="body-base text-stone-600 leading-relaxed">
              At Prithira Global, we specialize in supplying and exporting biodegradable 
              and compostable products made from natural materials. Our solutions are ideal 
              for restaurants, caterers, wholesalers, and international buyers seeking 
              sustainable alternatives to plastic packaging.
            </p>
          </div>

          {/* Why Choose Us */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
            {[
              {
                icon: Leaf,
                title: "100% Eco-Friendly",
                desc: "Biodegradable materials",
              },
              {
                icon: Globe,
                title: "Export-Ready",
                desc: "Global supply capability",
              },
              {
                icon: Package,
                title: "Bulk Orders",
                desc: "Large volume available",
              },
              {
                icon: Recycle,
                title: "Plastic-Free",
                desc: "Sustainable alternatives",
              },
              {
                icon: Users,
                title: "Trusted Partner",
                desc: "By businesses worldwide",
              },
            ].map((item, index) => (
              <div
                key={index}
                data-testid={`why-choose-${index}`}
                className="text-center p-6 rounded-2xl bg-sand-200/30 hover:bg-sand-200/60 transition-colors"
              >
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-primary/10 mb-4">
                  <item.icon className="h-6 w-6 text-primary" strokeWidth={1.5} />
                </div>
                <h3 className="font-heading text-lg text-stone-900 mb-2">
                  {item.title}
                </h3>
                <p className="text-stone-600 font-light text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Products Preview - Bento Grid */}
      <section data-testid="products-preview" className="section-spacing bg-sand-200/30">
        <div className="container-custom">
          <div className="text-center mb-16">
            <span className="caption text-primary mb-4 block">Our Products</span>
            <h2 className="heading-lg text-stone-900 mb-6">
              Eco-Friendly <span className="italic">Packaging</span> Solutions
            </h2>
            <p className="body-base text-stone-600 max-w-2xl mx-auto">
              Premium biodegradable products designed for sustainability without compromising
              on quality or functionality.
            </p>
          </div>

          {/* Bento Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Bagasse */}
            <div className="group relative overflow-hidden rounded-2xl bg-white border border-stone-100 shadow-sm hover:shadow-md transition-all">
              <div className="aspect-[4/3] overflow-hidden">
                <img
                  src={IMAGES.bagasse}
                  alt="Bagasse Tableware"
                  className="w-full h-full object-cover img-scale"
                />
              </div>
              <div className="p-8">
                <span className="caption text-terra-500 mb-2 block">Sugarcane Fiber</span>
                <h3 className="font-heading text-2xl text-stone-900 mb-3">
                  Bagasse Tableware
                </h3>
                <p className="text-stone-600 font-light text-sm mb-4">
                  Strong, compostable, heat resistant packaging made from sugarcane
                  residue. Perfect for food service and takeaway.
                </p>
                <Link
                  to="/products"
                  className="inline-flex items-center text-primary text-sm font-medium hover:underline"
                >
                  Learn More <ChevronRight className="h-4 w-4 ml-1" />
                </Link>
              </div>
            </div>

            {/* Areca */}
            <div className="group relative overflow-hidden rounded-2xl bg-white border border-stone-100 shadow-sm hover:shadow-md transition-all">
              <div className="aspect-[4/3] overflow-hidden">
                <img
                  src={IMAGES.areca}
                  alt="Areca Leaf Products"
                  className="w-full h-full object-cover img-scale"
                />
              </div>
              <div className="p-8">
                <span className="caption text-terra-500 mb-2 block">Palm Leaves</span>
                <h3 className="font-heading text-2xl text-stone-900 mb-3">
                  Areca Leaf Tableware
                </h3>
                <p className="text-stone-600 font-light text-sm mb-4">
                  Chemical-free, rustic elegance from naturally fallen palm leaves.
                  Perfect for hospitality and premium eco dining.
                </p>
                <Link
                  to="/products"
                  className="inline-flex items-center text-primary text-sm font-medium hover:underline"
                >
                  Explore <ChevronRight className="h-4 w-4 ml-1" />
                </Link>
              </div>
            </div>

            {/* Biodegradable Bags */}
            <div className="group relative overflow-hidden rounded-2xl bg-white border border-stone-100 shadow-sm hover:shadow-md transition-all">
              <div className="aspect-[4/3] overflow-hidden">
                <img
                  src={IMAGES.bioBags}
                  alt="Biodegradable Bags"
                  className="w-full h-full object-cover img-scale"
                />
              </div>
              <div className="p-8">
                <span className="caption text-terra-500 mb-2 block">Cornstarch Based</span>
                <h3 className="font-heading text-2xl text-stone-900 mb-3">
                  Biodegradable Bags
                </h3>
                <p className="text-stone-600 font-light text-sm mb-4">
                  Plant-derived carry bags and garbage bags designed to replace
                  single-use plastics responsibly.
                </p>
                <Link
                  to="/products"
                  className="inline-flex items-center text-primary text-sm font-medium hover:underline"
                >
                  Explore <ChevronRight className="h-4 w-4 ml-1" />
                </Link>
              </div>
            </div>

            {/* Bio Cutlery */}
            <div className="group relative overflow-hidden rounded-2xl bg-white border border-stone-100 shadow-sm hover:shadow-md transition-all">
              <div className="aspect-[4/3] overflow-hidden">
                <img
                  src={IMAGES.bioCutlery}
                  alt="Bio Cutlery"
                  className="w-full h-full object-cover img-scale"
                />
              </div>
              <div className="p-8">
                <span className="caption text-terra-500 mb-2 block">Cornstarch Based</span>
                <h3 className="font-heading text-2xl text-stone-900 mb-3">
                  Bio Cutlery
                </h3>
                <p className="text-stone-600 font-light text-sm mb-4">
                  Eco-friendly cutlery made from cornstarch. Sustainable alternative
                  to plastic utensils for all occasions.
                </p>
                <Link
                  to="/products"
                  className="inline-flex items-center text-primary text-sm font-medium hover:underline"
                >
                  Explore <ChevronRight className="h-4 w-4 ml-1" />
                </Link>
              </div>
            </div>

            {/* Coconut Scrubber */}
            <div className="md:col-span-2 group relative overflow-hidden rounded-2xl bg-stone-900 text-white">
              <div className="grid md:grid-cols-2 gap-8">
                <div className="p-8 md:p-12 flex flex-col justify-center">
                  <span className="caption text-terra-400 mb-4 block">Natural Cleaning</span>
                  <h3 className="font-heading text-3xl mb-4">
                    Coconut Coir Scrubbers
                  </h3>
                  <p className="text-stone-400 font-light mb-6">
                    100% natural fiber scrubbers made from coconut coir. Effective, 
                    durable, and completely biodegradable with no microplastics.
                  </p>
                  <Link to="/products">
                    <Button
                      variant="outline"
                      className="rounded-full px-6 py-5 text-sm border-white text-white hover:bg-white hover:text-stone-900 w-fit"
                    >
                      View Products
                    </Button>
                  </Link>
                </div>
                <div className="aspect-[4/3] md:aspect-auto overflow-hidden">
                  <img
                    src={IMAGES.coconutScrubber}
                    alt="Coconut Coir Scrubber"
                    className="w-full h-full object-cover img-scale"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sustainability Preview */}
      <section className="section-spacing bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div className="order-2 lg:order-1">
              <span className="caption text-primary mb-4 block">Our Commitment</span>
              <h2 className="heading-lg text-stone-900 mb-6">
                From Indian <span className="italic">Soil</span> to Global Tables
              </h2>
              <p className="body-base text-stone-600 mb-8">
                Every product we create represents our commitment to environmental
                responsibility. We transform agricultural waste into premium
                packaging solutions, reducing plastic dependency while supporting
                sustainable farming communities.
              </p>
              <ul className="space-y-4 mb-8">
                {[
                  "Agricultural waste materials",
                  "Complete compostable lifecycle",
                  "Reduced carbon footprint",
                  "Responsible supply chain",
                ].map((item, index) => (
                  <li key={index} className="flex items-center gap-3 text-stone-700">
                    <div className="w-2 h-2 rounded-full bg-primary" />
                    <span className="font-light">{item}</span>
                  </li>
                ))}
              </ul>
              <Link
                to="/sustainability"
                onClick={() => handleCTAClick("sustainability")}
              >
                <Button
                  data-testid="sustainability-cta"
                  className="rounded-full px-8 py-6 text-base font-medium bg-primary text-primary-foreground hover:bg-primary/90 btn-lift"
                >
                  Our Sustainability Story
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
            <div className="order-1 lg:order-2">
              <div className="relative rounded-2xl overflow-hidden bg-stone-50">
                <img
                  src={IMAGES.sustainability}
                  alt="Sustainability — Our Commitment"
                  className="w-full h-auto object-contain"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-spacing bg-primary text-white">
        <div className="container-custom text-center">
          <h2 className="heading-lg mb-6">
            Partner With Us for <span className="italic">Sustainable</span> Packaging
          </h2>
          <p className="body-lg text-white/80 mb-8 max-w-2xl mx-auto">
            Join the global movement towards sustainable packaging. Get premium 
            biodegradable solutions for your business.
          </p>
          
          {/* Contact Info */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-6 mb-10">
            <a href="mailto:info@prithiraglobal.com" className="flex items-center gap-2 text-white/90 hover:text-white">
              <Mail className="h-5 w-5" />
              <span>info@prithiraglobal.com</span>
            </a>
            <a href="tel:+919674010032" className="flex items-center gap-2 text-white/90 hover:text-white">
              <Phone className="h-5 w-5" />
              <span>+91 9674010032</span>
            </a>
            <a href="tel:+919830195737" className="flex items-center gap-2 text-white/90 hover:text-white">
              <Phone className="h-5 w-5" />
              <span>+91 9830195737</span>
            </a>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/partner" onClick={() => handleCTAClick("cta_partner")}>
              <Button
                data-testid="cta-partner-btn"
                className="rounded-full px-8 py-6 text-base font-medium bg-white text-primary hover:bg-stone-100 btn-lift"
              >
                Partner With Us
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/contact" onClick={() => handleCTAClick("cta_contact")}>
              <Button
                variant="outline"
                className="rounded-full px-8 py-6 text-base font-medium border-2 border-white text-white hover:bg-white/10"
              >
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
